import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
  Router,
  UrlSegment,
  Route,
} from '@angular/router';
import { filter, first, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationService } from '@shared/api/authentication.service';
import { UserState } from '@shared/states/user.state';
import { UserModel } from '@shared/types/user';
import { SettingsService } from '@api/settings.service';
@Injectable()
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private auth: AuthenticationService,
    private userState: UserState,
    private settingsService: SettingsService,
  ) {}

  private authSubscribeLogic(result: UserModel): void {
    if (result) {
      this.userState.user$.next(result);
      this.userState.userIsLogin$.next(true);
      this.router.navigate(['/']);
    }
  }

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> {
    if (route.queryParams['isStaff']) {
      this.settingsService.employeeEntryMethod().subscribe(() => {
        location.replace('/');
      });
    }

    if (route.queryParams['source']) {
      this.settingsService.savedParentUrl.next(route.queryParams['source']);
    }

    const isDashboard = state.url.startsWith('/dashboard');
    const isSharedSketch = state.url.startsWith('/sketch/shared/');

    return this.auth.authCheck().pipe(
      switchMap((valid) => {
        if (valid) {
          this.userState.userIsLogin$.next(true);

          if (!isDashboard) {
            return of(true);
          }

          // userState.user$ заполняется асинхронно через getProfile() в app.ts
          // после того как userIsLogin$.next(true) выше. Ждём первого непустого значения.
          return this.userState.user$.pipe(
            filter((user) => !!user),
            first(),
            map((user) => {
              if (user.anonymous) {
                this.router.navigate(['/sketch']);
                return false;
              }
              return true;
            }),
          );
        }

        return this.auth.anonymousLogin().pipe(
          first(),
          map((result) => {
            if (result?.anonymous && isDashboard) {
              this.userState.user$.next(result);
              this.userState.userIsLogin$.next(true);
              this.router.navigate(['/sketch']);
              return false;
            }

            // По ссылке общего доступа остаёмся на месте: authSubscribeLogic
            // уводит на '/', и токен из ссылки потерялся бы.
            if (result && isSharedSketch) {
              this.userState.user$.next(result);
              this.userState.userIsLogin$.next(true);
              return true;
            }

            this.authSubscribeLogic(result);

            return !!result;
          }),
        );
      }),
    );
  }

  public canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> {
    return of(false);
  }

  public canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
    return of(false);
  }
}
